import { useState } from "react";
import { useCreateContract } from "@/hooks/useContracts";
import { useDeals } from "@/hooks/useDeals";
import { useProposals } from "@/hooks/useProposals";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileSignature } from "lucide-react";

export function AddContractDialog() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [dealId, setDealId] = useState("");
  const [proposalId, setProposalId] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const createContract = useCreateContract();
  const { data: deals } = useDeals();
  const { data: proposals } = useProposals();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    await createContract.mutateAsync({
      title,
      deal_id: dealId || undefined,
      proposal_id: proposalId || undefined,
      signer_name: signerName || undefined,
      signer_email: signerEmail || undefined,
      expires_at: expiresAt || undefined,
    });

    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setTitle("");
    setDealId("");
    setProposalId("");
    setSignerName("");
    setSignerEmail("");
    setExpiresAt("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Contract
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5" />
            Create Contract
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Contract Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Service Agreement - Acme Corp"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="deal">Associated Deal</Label>
            <Select value={dealId || "none"} onValueChange={(v) => setDealId(v === "none" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a deal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {deals?.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    {deal.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="proposal">From Proposal</Label>
            <Select value={proposalId || "none"} onValueChange={(v) => setProposalId(v === "none" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a proposal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {proposals?.map((proposal) => (
                  <SelectItem key={proposal.id} value={proposal.id}>
                    {proposal.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="signer-name">Signer Name</Label>
              <Input
                id="signer-name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="John Smith"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signer-email">Signer Email</Label>
              <Input
                id="signer-email"
                type="email"
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="expires">Expires On</Label>
            <Input
              id="expires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createContract.isPending}>
              {createContract.isPending ? "Creating..." : "Create Contract"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
